
import { useState, useEffect, useCallback, useMemo } from "react";
import CustomerModal from "./CustomerModal.jsx";
import "./CustomerModal.css";
import ViewCustomerModal from "./ViewCustomerModal";
import {
  addCustomer,
  getCustomers,
  getCars,
  updateCustomer,
  deleteCustomer,
  completeCustomer
} from "../services/api";
import CompleteModal from "../components/CompleteModal";
import toast, { Toaster } from "react-hot-toast";
import { parseDateTimeLocalValue } from "../utils/dateTime";

// Utility functions must be defined before component usage
const formatDateShort = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short"
  });
};

const formatDateFull = (date) => {
  if (!date) return "-";
  return new Date(date)
    .toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    })
    .toLowerCase();
};

const getBookingStatus = (customer) => {
  if (!customer?.pickupDate || !customer?.dropDate) {
    return {
      label: "Unknown",
      color: "#64748b",
      background: "#f1f5f9"
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pickupDate = new Date(customer.pickupDate);
  pickupDate.setHours(0, 0, 0, 0);

  const dropDate = new Date(customer.dropDate);
  dropDate.setHours(0, 0, 0, 0);

  if (today < pickupDate) {
    return {
      label: "Upcoming",
      color: "#f59e0b",
      background: "#fffbeb"
    };
  }

  if (today > dropDate) {
    return {
      label: "Completed",
      color: "#ef4444",
      background: "#fef2f2"
    };
  }

  return {
    label: "Active",
    color: "#22c55e",
    background: "#f0fdf4"
  };
};

const getBookingStatusKey = (customer) => getBookingStatus(customer).label.toLowerCase();

const getBookingSortTime = (customer) => {
  const dateValue = customer?.pickupDate || customer?.dropDate;
  const timeValue = dateValue ? new Date(dateValue).getTime() : 0;
  return Number.isFinite(timeValue) ? timeValue : 0;
};

const statusPriority = {
  active: 0,
  upcoming: 1,
  completed: 2,
  unknown: 3
};

const buildWhatsAppUrl = (phone, message) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;

  const normalized = digits.length === 10
    ? `91${digits}`
    : digits.startsWith("91")
      ? digits
      : digits;

  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
};

export default function Customer() {
  const itemsPerPage = 10;
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeCustomerData, setCompleteCustomerData] = useState(null);
  // Calculate per hour/day for CompleteModal (amount = per-day rate)
  const getPerHourDay = (customer) => {
    if (!customer?.pickupDate || !customer?.dropDate || !customer?.perDayRate) return { perHour: 0, perDay: 0 };
    const perDayRate = Number(customer.perDayRate);
    const perHourRate = (perDayRate / 24).toFixed(2);
    return {
      perHour: perHourRate,
      perDay: perDayRate
    };
  };
  const [previewImage, setPreviewImage] = useState(null);
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    car: "",
    regNo: "",
    pickupDate: "",
    dropDate: "",
    amount: "",
    advanceAmount: ""
  });

  const [files, setFiles] = useState({});
  const [data, setData] = useState([]);
  const [cars, setCars] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [editId, setEditId] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);
  const [whatsAppBooking, setWhatsAppBooking] = useState(null);
  const [showCompleteWhatsAppPrompt, setShowCompleteWhatsAppPrompt] = useState(false);
  const [completeWhatsAppData, setCompleteWhatsAppData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // 📥 FETCH CUSTOMERS
  const fetchCustomers = async () => {
    try {
      const res = await getCustomers();
      setData(res.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    const visibleCustomers = statusFilter === "all"
      ? data
      : data.filter((customer) => getBookingStatusKey(customer) === statusFilter);

    return [...visibleCustomers].sort((a, b) => {
      const statusDiff = (statusPriority[getBookingStatusKey(a)] ?? 3) - (statusPriority[getBookingStatusKey(b)] ?? 3);
      if (statusDiff !== 0) return statusDiff;

      const dateDiff = getBookingSortTime(a) - getBookingSortTime(b);
      if (dateDiff !== 0) return dateDiff;

      return String(a.fullName || "").localeCompare(String(b.fullName || ""));
    });
  }, [data, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / itemsPerPage));

  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCustomers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCustomers, currentPage]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // 🚗 FETCH CARS
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await getCars();
        setCars(res.data);
      } catch {
        toast.error("Failed to load cars");
      }
    };
    fetchCars();
  }, []);

  // 🔥 AVAILABILITY CHECK (FIXED WITH useCallback)
  const checkAvailability = useCallback(async () => {
    if (!form.car || !form.pickupDate || !form.dropDate) {
      setAvailability(null);
      return;
    }

    try {
      const res = await getCustomers();
      const bookings = res.data;

      const newStart = parseDateTimeLocalValue(form.pickupDate)?.getTime();
      const newEnd = parseDateTimeLocalValue(form.dropDate)?.getTime();

      if (!newStart || !newEnd) {
        setAvailability(null);
        return;
      }

      const BUFFER = 3 * 60 * 60 * 1000;

      const conflict = bookings.find((b) => {
        if (b.car !== form.car) return false;
        if (editId && b._id === editId) return false;

        const existingStart = parseDateTimeLocalValue(b.pickupDate)?.getTime();
        const existingEnd = parseDateTimeLocalValue(b.dropDate)?.getTime();

        if (!existingStart || !existingEnd) return false;

        return newStart < existingEnd + BUFFER && newEnd > existingStart - BUFFER;
      });

      if (conflict) {
        setAvailability({ status: "blocked", data: conflict });
      } else {
        setAvailability({ status: "available" });
      }

    } catch {
      console.log("availability check failed");
    }
  }, [form.car, form.pickupDate, form.dropDate, editId]);

  // ⏱️ DEBOUNCED EFFECT
  useEffect(() => {
    const timer = setTimeout(() => {
      checkAvailability();
    }, 300);

    return () => clearTimeout(timer);
  }, [checkAvailability]);

  // 💰 PRICE CALCULATION (amount = per-day rate)
  const calculatePrice = useCallback(() => {
    if (!form.pickupDate || !form.dropDate || !form.amount) {
      setPriceInfo(null);
      return;
    }

    const start = parseDateTimeLocalValue(form.pickupDate);
    const end = parseDateTimeLocalValue(form.dropDate);

    if (!start || !end) {
      setPriceInfo(null);
      return;
    }

    const diffMs = end - start;
    if (diffMs <= 0) {
      setPriceInfo(null);
      return;
    }

    const totalHours = diffMs / (1000 * 60 * 60);
    const totalDays = totalHours / 24;
    const perDayRate = Number(form.amount); // Amount is per-day rate
    const perHourRate = (perDayRate / 24).toFixed(2);
    const totalAmount = (perDayRate * totalDays).toFixed(2);

    setPriceInfo({
      perHour: perHourRate,
      perDay: perDayRate,
      totalHours: totalHours.toFixed(1),
      totalDays: totalDays.toFixed(1),
      totalAmount: totalAmount
    });
  }, [form.amount, form.pickupDate, form.dropDate]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  // 🧠 VALIDATION
  const validate = () => {
    if (!form.fullName?.trim()) return "Please enter the customer name";
    if (!form.phone) return "Please enter the mobile number";
    if (!form.car) return "Please select a car";
    if (!form.pickupDate) return "Please select a pickup date and time";
    if (!form.dropDate) return "Please select a drop date and time";

    const start = parseDateTimeLocalValue(form.pickupDate);
    const end = parseDateTimeLocalValue(form.dropDate);

    if (!start || !end) return "Please enter valid pickup and drop date values";

    if (end <= start) return "Drop date and time must be later than pickup";

    return null;
  };

  // 💾 SUBMIT WITH RETRY & PROPER DATE FORMATTING
  const handleSubmit = async (retryCount = 0) => {
    if (availability?.status === "blocked") {
      return toast.error("Car not available");
    }

    const err = validate();
    if (err) return toast.error(err);

    try {
      const formData = new FormData();
      const totalAmount = priceInfo && priceInfo.totalAmount ? priceInfo.totalAmount : form.amount;
      const advanceAmt = Number(form.advanceAmount || 0);
      const pendingAmt = Number(totalAmount) - advanceAmt;

      // 🔥 Convert datetime-local to ISO format for backend
      const pickupDate = parseDateTimeLocalValue(form.pickupDate);
      const dropDate = parseDateTimeLocalValue(form.dropDate);

      const bookingSnapshot = {
        fullName: form.fullName,
        phone: form.phone,
        car: form.car,
        regNo: form.regNo,
        pickupDate: form.pickupDate,
        dropDate: form.dropDate,
        amount: totalAmount,
        advanceAmount: advanceAmt,
        pendingAmount: pendingAmt
      };
      
      // Add basic fields with proper ISO format for backend
      formData.append('fullName', form.fullName);
      formData.append('phone', form.phone);
      formData.append('car', form.car);
      formData.append('regNo', form.regNo);
      formData.append('pickupDate', pickupDate?.toISOString() || form.pickupDate);
      formData.append('dropDate', dropDate?.toISOString() || form.dropDate);
      
      // Add pricing info (use priceInfo if available, otherwise use form.amount)
      if (priceInfo && priceInfo.totalAmount) {
        formData.append('perDayRate', Number(priceInfo.perDay) || 0);
        formData.append('totalDays', Number(priceInfo.totalDays) || 1);
        formData.append('totalHours', Number(priceInfo.totalHours) || 24);
        formData.append('amount', Number(priceInfo.totalAmount) || 0);
      } else {
        // Fallback: use form amount (shouldn't happen if user fills fields correctly)
        formData.append('perDayRate', Number(form.amount) || 0);
        formData.append('totalDays', 1);
        formData.append('totalHours', 24);
        formData.append('amount', Number(form.amount) || 0);
      }
      
      // Add advance amount
      formData.append('advanceAmount', advanceAmt);
      
      Object.keys(files).forEach(k => {
        const file = files[k];
        // Only append if it's a File object (not existing document metadata)
        if (file instanceof File) {
          formData.append(k, file);
        }
      });

      const submitFn = editId ? () => updateCustomer(editId, formData) : () => addCustomer(formData);

      try {
        await submitFn();
        toast.success(editId ? "Updated successfully" : "Booking added successfully");
        setShowModal(false);
        setEditId(null);

        setForm({
          fullName: "",
          phone: "",
          car: "",
          regNo: "",
          pickupDate: "",
          dropDate: "",
          amount: "",
          advanceAmount: ""
        });

        setFiles({});
        
        if (!editId) {
          setWhatsAppBooking(bookingSnapshot);
          setShowWhatsAppPrompt(true);
        }

        fetchCustomers();

      } catch (submitErr) {
        // Retry logic for network errors
        if (retryCount < 2 && (submitErr?.code === 'ECONNABORTED' || submitErr?.message?.includes('Network'))) {
          console.warn(`Retry attempt ${retryCount + 1}...`);
          toast.loading("Retrying...");
          setTimeout(() => handleSubmit(retryCount + 1), 2000);
          return;
        }

        throw submitErr;
      }

    } catch (err) {
      console.error("❌ Submission error:", err);
      
      // User-friendly error messages
      let errorMsg = "Something went wrong";
      
      if (err?.message?.includes('Network') || err?.code === 'ECONNABORTED') {
        errorMsg = "Network error. Check your internet connection and try again.";
      } else if (err?.response?.status === 400) {
        errorMsg = err?.response?.data?.msg || "Invalid booking details. Please check and try again.";
      } else if (err?.response?.status === 409) {
        errorMsg = "Car not available for selected time. Please choose different dates.";
      } else if (err?.response?.status === 500) {
        errorMsg = "Server error. Please try again later.";
      } else {
        errorMsg = err?.response?.data?.msg || err?.response?.data?.message || err?.message || errorMsg;
      }
      
      toast.error(errorMsg);
    }
  };

  const handleSendWhatsApp = () => {
    if (!whatsAppBooking) return;

    const messageParts = [
      `Hello ${whatsAppBooking.fullName}`,
      `Your booking with *Drive Onyx* is confirmed.`,
      `Car: ${whatsAppBooking.car}`,
      `Reg. No.: ${whatsAppBooking.regNo}`,
      `Pickup: ${formatDateFull(whatsAppBooking.pickupDate)}`,
      `Drop: ${formatDateFull(whatsAppBooking.dropDate)}`,
      `Total Amount: ₹${Number(whatsAppBooking.amount || 0).toLocaleString("en-IN")}`
    ];

    // Add advance amount if provided
    if (whatsAppBooking.advanceAmount > 0) {
      messageParts.push(`Advance Paid: ₹${Number(whatsAppBooking.advanceAmount).toLocaleString("en-IN")}`);
      messageParts.push(`Pending: ₹${Number(whatsAppBooking.pendingAmount).toLocaleString("en-IN")}`);
    }

    messageParts.push(
      `Wishing you a happy journey ahead and a smooth, comfortable ride.`,
      `Contact: +91 9823640666 | darshan@driveonyx.in`
    );

    const message = messageParts.join("\n");

    const whatsappUrl = buildWhatsAppUrl(whatsAppBooking.phone, message);
    if (!whatsappUrl) {
      toast.error("Customer phone number is invalid");
      return;
    }

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setShowWhatsAppPrompt(false);
    setWhatsAppBooking(null);
  };

  const handleSendCompleteWhatsApp = () => {
    if (!completeWhatsAppData) return;

    const refund = Number(completeWhatsAppData.refundAmount) || 0;
    const extra = Number(completeWhatsAppData.addedAmount) || 0;
    const final = (Number(completeWhatsAppData.originalAmount) - refund + extra).toFixed(2);

    const messageParts = [
      `Hello ${completeWhatsAppData.fullName}`,
      `Your booking with *Drive Onyx* is complete.`,
      `Car: ${completeWhatsAppData.car}`,
      `Reg. No.: ${completeWhatsAppData.regNo}`,
      `Original Amount: ₹${Number(completeWhatsAppData.originalAmount).toLocaleString("en-IN")}`
    ];

    if (refund > 0) {
      messageParts.push(`Refund (${completeWhatsAppData.refundDescription}): -₹${refund.toLocaleString("en-IN")}`);
    }

    if (extra > 0) {
      messageParts.push(`Extra Charges (${completeWhatsAppData.otherExpenses}): +₹${extra.toLocaleString("en-IN")}`);
    }

    messageParts.push(
      `Final Amount: ₹${Number(final).toLocaleString("en-IN")}`,
      `Thank you for choosing *Drive Onyx*! We appreciate your business.`,
      `We hope you had a smooth and comfortable ride.`,
      `Contact: +91 9823640666 | darshan@driveonyx.in`
    );

    const message = messageParts.join("\n");
    const whatsappUrl = buildWhatsAppUrl(completeWhatsAppData.phone, message);
    if (!whatsappUrl) {
      toast.error("Customer phone number is invalid");
      return;
    }

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setShowCompleteWhatsAppPrompt(false);
    setCompleteWhatsAppData(null);
  };

  return (
  <>
    <Toaster />

    <div className="customer-wrapper">

      {/* HEADER */}
      <div className="top-bar">
        <div>
          <h1>Customer Dashboard</h1>
          <p>Manage bookings, customers & vehicle allocation</p>
        </div>

        <button
          className="primary-btn"
          onClick={() => setShowModal(true)}
        >
          + New Booking
        </button>
      </div>

      {/* TABLE CARD */}
      <div className="card">

        <div className="card-header">
          <h3>All Customers</h3>
          <span>{filteredCustomers.length} Records{statusFilter !== "all" ? ` (${statusFilter})` : ""}</span>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "0 18px 14px" }}>
          {[
            { key: "all", label: "All", color: "#334155", background: "#f8fafc" },
            { key: "active", label: "Active", color: "#16a34a", background: "#f0fdf4" },
            { key: "upcoming", label: "Upcoming", color: "#f59e0b", background: "#fffbeb" },
            { key: "completed", label: "Completed", color: "#ef4444", background: "#fef2f2" }
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setStatusFilter(item.key)}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: "1px solid " + (statusFilter === item.key ? item.color : "#cbd5e1"),
                background: statusFilter === item.key ? item.background : "#fff",
                color: item.color,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: statusFilter === item.key ? `0 4px 12px ${item.color}18` : "none"
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="table-container">
          <table className="pro-table">

            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Car</th>
                <th>Reg. No</th>
                <th>Pickup</th>
                <th>Drop</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Advance</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty">
                    No bookings yet
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty">
                    No {statusFilter === "all" ? "records" : statusFilter} bookings found
                  </td>
                </tr>
              ) : (
                paginatedCustomers.map((c) => (
                  <tr key={c._id}>

                    <td>
                      <div className="user-cell">
                        <div className="avatar">
                          {c.fullName?.charAt(0)}
                        </div>
                        <div>
                          <strong>{c.fullName}</strong>
                        </div>
                      </div>
                    </td>

                    <td>{c.phone}</td>

                    <td>
                      <span className="car-tag">
                        {c.car}
                      </span>
                    </td>

                    <td style={{ fontWeight: 500, color: "#334155" }}>
                      {c.regNo}
                    </td>

                    <td className="green">
                      {formatDateShort(c.pickupDate)}
                    </td>

                    <td className="red">
                      {formatDateShort(c.dropDate)}
                    </td>

                    <td>
                      {(() => {
                        const status = getBookingStatus(c);

                        return (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 999, background: status.background, color: status.color, fontWeight: 700, fontSize: 13 }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: status.color, display: "inline-block", boxShadow: `0 0 0 3px ${status.color}22` }} />
                            {status.label}
                          </span>
                        );
                      })()}
                    </td>

                    <td className="amount">
                      ₹{c.amount}
                    </td>

                    <td style={{ color: c.advanceAmount > 0 ? "#22c55e" : "#999", fontWeight: 600 }}>
                      ₹{c.advanceAmount || 0}
                    </td>

                    <td style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="view-btn"
                        onClick={() => setViewData(c)}
                      >
                        View
                      </button>
                      <button
                        className="complete-btn"
                        onClick={() => {
                          setCompleteCustomerData(c);
                          setShowCompleteModal(true);
                        }}
                        style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 14px', cursor: 'pointer' }}
                      >
                        Complete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>

        {data.length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 18px 4px" }}>
            <div style={{ color: "#64748b", fontSize: 14 }}>
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, data.length)}-{Math.min(currentPage * itemsPerPage, data.length)} of {data.length}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: currentPage === 1 ? "#f8fafc" : "#fff",
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontWeight: 600
                }}
              >
                Prev
              </button>
              <div style={{ display: "flex", alignItems: "center", padding: "0 4px", color: "#334155", fontWeight: 600 }}>
                Page {currentPage} of {totalPages}
              </div>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: currentPage === totalPages ? "#f8fafc" : "#fff",
                  color: currentPage === totalPages ? "#94a3b8" : "#334155",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontWeight: 600
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Complete Modal */}
      <CompleteModal
        show={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        customer={completeCustomerData}
        perHour={getPerHourDay(completeCustomerData).perHour}
        perDay={getPerHourDay(completeCustomerData).perDay}
        onSubmit={async ({ otherExpenses, addedAmount, refundDescription, refundAmount }) => {
          try {
            await completeCustomer({
              customerId: completeCustomerData._id,
              addedAmount,
              otherExpenses,
              refundDescription,
              refundAmount
            });
            toast.success("Completed!");
            setShowCompleteModal(false);
            setCompleteWhatsAppData({
              fullName: completeCustomerData.fullName,
              phone: completeCustomerData.phone,
              car: completeCustomerData.car,
              regNo: completeCustomerData.regNo,
              originalAmount: completeCustomerData.amount,
              refundDescription,
              refundAmount: Number(refundAmount) || 0,
              otherExpenses,
              addedAmount: Number(addedAmount) || 0
            });
            setShowCompleteWhatsAppPrompt(true);
            setCompleteCustomerData(null);
            fetchCustomers();
          } catch (err) {
            toast.error(err?.response?.data?.message || "Error");
          }
        }}
      />

      {showWhatsAppPrompt && whatsAppBooking && (
        <div
          className="modal-overlay"
          style={{ zIndex: 2000, background: "rgba(15,23,42,0.72)" }}
        >
          <div
            className="modal"
            style={{
              width: "100%",
              maxWidth: 480,
              background: "#fff",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 24px 70px rgba(15,23,42,0.25)"
            }}
          >
            <h2 style={{ marginBottom: 10, color: "#0f172a" }}>Send WhatsApp Confirmation?</h2>
            <p style={{ color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
              Booking saved successfully. Do you want to send a ready-made confirmation message on WhatsApp to {whatsAppBooking.phone}?
            </p>

            <div style={{ background: "#f8fafc", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>Message preview</div>
              <div style={{ whiteSpace: "pre-line", color: "#334155", fontSize: 14, lineHeight: 1.6 }}>
                {(() => {
                  const parts = [
                    `Hello ${whatsAppBooking.fullName} 🌸`,
                    `Your booking with *Drive Onyx* is confirmed.`,
                    `Car: ${whatsAppBooking.car}`,
                    `Reg. No.: ${whatsAppBooking.regNo}`,
                    `Pickup: ${formatDateFull(whatsAppBooking.pickupDate)}`,
                    `Drop: ${formatDateFull(whatsAppBooking.dropDate)}`,
                    `Total Amount: ₹${Number(whatsAppBooking.amount || 0).toLocaleString("en-IN")}`
                  ];

                  if (whatsAppBooking.advanceAmount > 0) {
                    parts.push(`Advance Paid: ₹${Number(whatsAppBooking.advanceAmount).toLocaleString("en-IN")}`);
                    parts.push(`Pending: ₹${Number(whatsAppBooking.pendingAmount).toLocaleString("en-IN")}`);
                  }

                  parts.push(
                    `Wishing you a happy journey ahead and a smooth, comfortable ride.`,
                    `Contact: +91 9823640666 | darshan@driveonyx.in`
                  );

                  return parts.join("\n");
                })()}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button
                type="button"
                onClick={() => {
                  setShowWhatsAppPrompt(false);
                  setWhatsAppBooking(null);
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  color: "#334155",
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleSendWhatsApp}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: "#22c55e",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 700
                }}
              >
                Send on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompleteWhatsAppPrompt && completeWhatsAppData && (
        <div
          className="modal-overlay"
          style={{ zIndex: 2000, background: "rgba(15,23,42,0.72)" }}
        >
          <div
            className="modal"
            style={{
              width: "100%",
              maxWidth: 480,
              background: "#fff",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 24px 70px rgba(15,23,42,0.25)"
            }}
          >
            <h2 style={{ marginBottom: 10, color: "#0f172a" }}>Send Completion Message?</h2>
            <p style={{ color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
              Would you like to send a completion summary and thank you message on WhatsApp to {completeWhatsAppData.phone}?
            </p>

            <div style={{ background: "#f8fafc", borderRadius: 12, padding: 16, marginBottom: 20, maxHeight: 300, overflowY: "auto" }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>Message preview</div>
              <div style={{ whiteSpace: "pre-line", color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                {(() => {
                  const refund = Number(completeWhatsAppData.refundAmount) || 0;
                  const extra = Number(completeWhatsAppData.addedAmount) || 0;
                  const final = (Number(completeWhatsAppData.originalAmount) - refund + extra).toFixed(2);

                  const parts = [
                    `Hello ${completeWhatsAppData.fullName}`,
                    `Your booking with *Drive Onyx* is complete.`,
                    `Car: ${completeWhatsAppData.car}`,
                    `Reg. No.: ${completeWhatsAppData.regNo}`,
                    `Original Amount: ₹${Number(completeWhatsAppData.originalAmount).toLocaleString("en-IN")}`
                  ];

                  if (refund > 0) {
                    parts.push(`Refund (${completeWhatsAppData.refundDescription}): -₹${refund.toLocaleString("en-IN")}`);
                  }

                  if (extra > 0) {
                    parts.push(`Extra Charges (${completeWhatsAppData.otherExpenses}): +₹${extra.toLocaleString("en-IN")}`);
                  }

                  parts.push(
                    `Final Amount: ₹${Number(final).toLocaleString("en-IN")}`,
                    `Thank you for choosing *Drive Onyx*! We appreciate your business.`,
                    `We hope you had a smooth and comfortable ride.`,
                    `Contact: +91 9823640666 | darshan@driveonyx.in`
                  );

                  return parts.join("\n");
                })()}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button
                type="button"
                onClick={() => {
                  setShowCompleteWhatsAppPrompt(false);
                  setCompleteWhatsAppData(null);
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  color: "#334155",
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSendCompleteWhatsApp}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: "#22c55e",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 700
                }}
              >
                Send on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALS */}
      <CustomerModal
        showModal={showModal}
        setShowModal={setShowModal}
        form={form}
        setForm={setForm}
        files={files}
        setFiles={setFiles}
        cars={cars}
        availability={availability}
        priceInfo={priceInfo}
        handleSubmit={handleSubmit}
        formatDateFull={formatDateFull}
        editId={editId}
      />

     <ViewCustomerModal
  viewData={viewData}
  setViewData={setViewData}
  formatDateFull={formatDateFull}
  deleteCustomer={deleteCustomer}
  fetchCustomers={fetchCustomers}
  setForm={setForm}
  setEditId={setEditId}
  setShowModal={setShowModal}
  setPreviewImage={setPreviewImage}
  setFiles={setFiles}
/>
{previewImage && (
  <div className="modal-overlay">

    {/* ❌ CLOSE BUTTON */}
    <span
      style={{
        position: "absolute",
        top: "20px",
        right: "30px",
        fontSize: "28px",
        color: "white",
        cursor: "pointer",
        zIndex: 1001
      }}
      onClick={() => setPreviewImage(null)}
    >
      ✖
    </span>

    {/* IMAGE */}
    <img
      src={previewImage}
      alt="preview"
      style={{
        maxWidth: "90%",
        maxHeight: "90%",
        borderRadius: "10px"
      }}
      onClick={(e) => e.stopPropagation()}
    />
  </div>
)}
    </div>
  </>
);
}